import { BusinessNameGeneratorContainer } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorContainer'
import { BusinessNameGeneratorUserPromptSummary } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorUserPromptSummary'
import { FreeToolLayout } from '@/React/FreeTool/Shared/Layout/FreeToolLayout'
import { Card } from '@/React/Shared/Component/Card'
import { Button } from '@/React/Shared/Component/Button'
import { IconNameEnum } from '@/React/Shared/Component/Icon'
import { RoutePathFreeToolEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
import { GoogleSocialAuthButton } from '@/React/App/User/Component/GoogleSocialAuthButton'
import { MicrosoftSocialAuthButton } from '@/React/App/User/Component/MicrosoftSocialAuthButton'
import { UrlFactory } from '@/Shared/Url/UrlFactory'
import {
    type BEShowBusinessNameGeneratorStepRegisterPagePropKeys,
    type BusinessNameGeneratorSessionRawData,
    type FreeToolSharedDataRawData,
} from '@/Shared/AutoGenerated/Main'
import FreeToolSharedData from '@/FreeTool/Shared/Entity/FreeToolSharedData'
import BusinessNameGeneratorSession from '@/FreeTool/BusinessNameGenerator/Entity/BusinessNameGeneratorSession'
import type { EnforceBEPagePropKeys } from '@/Shared/Helper/EnforceBEPagePropKeys'

type BusinessNameGeneratorStepRegisterPageProps = EnforceBEPagePropKeys<
    BEShowBusinessNameGeneratorStepRegisterPagePropKeys,
    {
        freeToolSharedDataRawData: FreeToolSharedDataRawData
        businessNameGeneratorSessionRawData: BusinessNameGeneratorSessionRawData
        googleSocialLoginEnabled: boolean
        microsoftSocialLoginEnabled: boolean
    }
>

export default function BusinessNameGeneratorStepRegisterPage({
    freeToolSharedDataRawData,
    businessNameGeneratorSessionRawData,
    googleSocialLoginEnabled,
    microsoftSocialLoginEnabled,
}: BusinessNameGeneratorStepRegisterPageProps) {
    const sharedData = FreeToolSharedData.hydrate(freeToolSharedDataRawData)

    const currentSession = BusinessNameGeneratorSession.hydrateSingle(
        businessNameGeneratorSessionRawData,
    )

    const urlFactory = new UrlFactory()

    const hasSocialLoginOptions =
        googleSocialLoginEnabled || microsoftSocialLoginEnabled

    return (
        <FreeToolLayout
            appName={sharedData.appName}
            flashStatus={sharedData.flashStatus}
            headerRight={
                <span className="text-xs text-gray-500 sm:text-sm">
                    Business name generator
                </span>
            }
            footerLeft={
                <Button
                    as="a"
                    href={
                        RoutePathFreeToolEnum.BusinessNameGenerator_StepResults
                    }
                    color="light"
                    iconName={IconNameEnum.ArrowLeft}
                >
                    Back
                </Button>
            }
        >
            <BusinessNameGeneratorContainer
                eyebrow="Your description is saved"
                title="Create your free account"
                description="Continue to your name ideas. You can set up the rest of your business later."
            >
                <div className="mx-auto max-w-xl">
                    <Card.Container>
                        <Card.Content>
                            {currentSession.userPrompt ? (
                                <BusinessNameGeneratorUserPromptSummary
                                    userPrompt={currentSession.userPrompt}
                                />
                            ) : null}

                            {hasSocialLoginOptions ? (
                                <div className="space-y-3">
                                    {googleSocialLoginEnabled ? (
                                        <GoogleSocialAuthButton
                                            url={urlFactory.makeGoogleOAuthBusinessNameGeneratorRegisterRedirectUrl()}
                                        >
                                            Continue with Google
                                        </GoogleSocialAuthButton>
                                    ) : null}

                                    {microsoftSocialLoginEnabled ? (
                                        <MicrosoftSocialAuthButton
                                            url={urlFactory.makeMicrosoftOAuthBusinessNameGeneratorRegisterRedirectUrl()}
                                        >
                                            Continue with Microsoft
                                        </MicrosoftSocialAuthButton>
                                    ) : null}
                                </div>
                            ) : null}

                            <p className="mt-5 text-center text-xs text-gray-500">
                                Already have an account? Continue with your
                                connected provider.
                            </p>
                        </Card.Content>
                    </Card.Container>
                </div>
            </BusinessNameGeneratorContainer>
        </FreeToolLayout>
    )
}
